import * as React from "react";
import { useQueries } from "@tanstack/react-query";
import type {
  TableFilterConfig,
  TableFilterFieldConfig,
  TableFilterSelectOption,
} from "@ibada/ui/components/data-table";
import { useDataTableQueryState, type DataTableQueryState } from "@ibada/ui/components/data-table";

import { useDebouncedValue } from "@/shared/hooks/use-debounced-value";
import { lookupQueryKeys } from "@/shared/hooks/use-lookup";
import { fetchLookup } from "@/shared/lib/api/lookups-api";
import { type TenantScopeContext } from "@/shared/model/tenant-scope-context";
import { type LookupType } from "@/shared/model/lookup-types";

export type TableQueryOptionStaticConfig = {
  type: "static";
  items: Array<{ value: string; labelKey?: string; label?: string }>;
};

export type TableQueryOptionCollectionConfig = {
  type: "collection";
  source: string;
  valueKey: string;
  labelKey: string;
  includeAllLabelKey?: string;
  includeAllValue?: string;
  sort?: "label" | "none";
};

export type TableQueryOptionConfig = TableQueryOptionStaticConfig | TableQueryOptionCollectionConfig;

export type TableQueryOperatorConfig = {
  value: string;
  labelKey: string;
};

type TableQueryBaseFieldConfig = {
  id: string;
  labelKey: string;
  testId?: string;
  placeholderKey?: string;
  descriptionKey?: string;
  hidden?: boolean;
  visibleWhen?: {
    key: string;
    equals: string;
  };
  disabled?: boolean;
  operators?: TableQueryOperatorConfig[];
  defaultOperator?: string;
};

export type TableQueryTextFieldConfig = TableQueryBaseFieldConfig & {
  type: "text";
  key: string;
  defaultValue?: string;
};

export type TableQuerySelectFieldConfig = TableQueryBaseFieldConfig & {
  type: "select";
  key: string;
  options: TableQueryOptionConfig;
  defaultValue?: string;
  creatable?: boolean;
  createLabelKey?: string;
};

export type TableQueryAsyncLookupConfig = {
  type: LookupType;
  searchParam?: "q" | "search";
  valueKey?: "value";
  labelKey?: "label";
  limit?: number;
  debounceMs?: number;
  activeOnly?: boolean;
  context?: TenantScopeContext;
  enabled?: boolean;
  searchPlaceholderKey?: string;
  mobileEmptyKey?: string;
  loadingMessageKey?: string;
};

export type TableQueryAsyncSelectFieldConfig = TableQueryBaseFieldConfig & {
  type: "asyncSelect";
  key: string;
  lookup: TableQueryAsyncLookupConfig;
  defaultValue?: string;
};

export type TableQueryMultiSelectFieldConfig = TableQueryBaseFieldConfig & {
  type: "multiSelect";
  key: string;
  options: TableQueryOptionConfig;
  defaultValues?: string[];
  emptyLabelKey?: string;
  creatable?: boolean;
  createLabelKey?: string;
};

export type TableQueryBooleanFieldConfig = TableQueryBaseFieldConfig & {
  type: "boolean";
  key: string;
  defaultValue?: "" | "true" | "false";
  trueLabelKey?: string;
  falseLabelKey?: string;
};

export type TableQueryDateTimeFieldConfig = TableQueryBaseFieldConfig & {
  type: "dateTime";
  key: string;
  defaultValue?: string;
};

export type TableQueryDateRangePresetId =
  | "today"
  | "last7Days"
  | "last30Days"
  | "last3Months"
  | "last6Months"
  | "last12Months"
  | "all";

export type TableQueryDateRangePresetConfig = {
  id: TableQueryDateRangePresetId;
  labelKey: string;
};

export type TableQueryDateRangeFieldConfig = {
  id: string;
  type: "dateRange";
  labelKey: string;
  descriptionKey?: string;
  hidden?: boolean;
  visibleWhen?: {
    key: string;
    equals: string;
  };
  disabled?: boolean;
  operators?: TableQueryOperatorConfig[];
  defaultOperator?: string;
  fromKey: string;
  toKey: string;
  fromLabelKey?: string;
  toLabelKey?: string;
  fromPlaceholderKey?: string;
  toPlaceholderKey?: string;
  fromDefaultValue?: string;
  toDefaultValue?: string;
  presets?: TableQueryDateRangePresetConfig[];
};

export type TableQueryNumberRangeFieldConfig = {
  id: string;
  type: "numberRange";
  labelKey: string;
  descriptionKey?: string;
  hidden?: boolean;
  visibleWhen?: {
    key: string;
    equals: string;
  };
  disabled?: boolean;
  operators?: TableQueryOperatorConfig[];
  defaultOperator?: string;
  minKey: string;
  maxKey: string;
  minLabelKey?: string;
  maxLabelKey?: string;
  minPlaceholderKey?: string;
  maxPlaceholderKey?: string;
  minDefaultValue?: string;
  maxDefaultValue?: string;
};

export type TableQueryFieldConfig =
  | TableQueryTextFieldConfig
  | TableQuerySelectFieldConfig
  | TableQueryAsyncSelectFieldConfig
  | TableQueryMultiSelectFieldConfig
  | TableQueryBooleanFieldConfig
  | TableQueryDateTimeFieldConfig
  | TableQueryDateRangeFieldConfig
  | TableQueryNumberRangeFieldConfig;

export type TableQueryConfig = {
  id: string;
  titleKey?: string;
  descriptionKey?: string;
  buttonLabelKey?: string;
  cancelLabelKey?: string;
  resetLabelKey?: string;
  applyLabelKey?: string;
  clearLabelKey?: string;
  search?: {
    placeholderKey?: string;
    testId?: string;
    debounceMs?: number;
    className?: string;
  };
  addFilterButtonTestId?: string;
  filters?: TableQueryFieldConfig[];
  defaultSortBy?: string;
  defaultSortOrder?: "asc" | "desc";
  defaultSearch?: string;
  defaultPage?: number;
  defaultLimit?: number;
  refreshButtonTestId?: string;
  refreshLabelKey?: string;
  urlMode?: "replace" | "push";
};

export type TableQueryOptionSources = Record<string, Array<Record<string, unknown>>>;

export type TableQueryLookupScope = {
  workspaceId?: string;
  branchId?: string | null;
  context?: TenantScopeContext;
};

type TranslateFn = (key: string, params?: Record<string, string>) => string;

type AsyncFilterRuntimeState = {
  isLoading: boolean;
  loadingMessage?: string;
  mobileEmpty?: string;
  onSearchValueChange: (value: string) => void;
  options: TableFilterSelectOption[];
  searchPlaceholder?: string;
  searchValue: string;
};

function resolveSelectOptions(
  config: TableQueryOptionConfig,
  t: TranslateFn,
  sources: TableQueryOptionSources,
): TableFilterSelectOption[] {
  if (config.type === "static") {
    return config.items.map((item) => ({
      value: item.value,
      label: item.label ?? (item.labelKey ? t(item.labelKey as never) : item.value),
    }));
  }

  const source = sources[config.source] ?? [];
  const options: TableFilterSelectOption[] = [];

  if (config.includeAllLabelKey) {
    options.push({
      value: config.includeAllValue ?? "",
      label: t(config.includeAllLabelKey as never),
    });
  }

  for (const item of source) {
    const rawValue = item[config.valueKey];
    const rawLabel = item[config.labelKey];
    if (rawValue === null || rawValue === undefined || rawLabel === null || rawLabel === undefined) {
      continue;
    }
    const value = typeof rawValue === "string" ? rawValue : String(rawValue);
    const label = typeof rawLabel === "string" ? rawLabel : String(rawLabel);
    if (!value.trim() || !label.trim()) {
      continue;
    }
    options.push({ value, label });
  }

  if (config.sort === "label") {
    options.sort((a, b) => a.label.localeCompare(b.label));
  }

  return options;
}

function resolveOperators(config: TableQueryOperatorConfig[] | undefined, t: TranslateFn) {
  if (!config?.length) return undefined;
  return config.map((operator) => ({
    value: operator.value,
    label: t(operator.labelKey as never),
  }));
}

function resolveBaseConfig(config: TableQueryBaseFieldConfig, t: TranslateFn) {
  return {
    id: config.id,
    label: t(config.labelKey as never),
    testId: config.testId,
    placeholder: config.placeholderKey ? t(config.placeholderKey as never) : undefined,
    description: config.descriptionKey ? t(config.descriptionKey as never) : undefined,
    hidden: config.hidden,
    visibleWhen: config.visibleWhen,
    disabled: config.disabled,
    operators: resolveOperators(config.operators, t),
    defaultOperator: config.defaultOperator,
  };
}

function safeParseSearchMap(value: string): Record<string, string> {
  try {
    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }

    return Object.entries(parsed).reduce<Record<string, string>>((acc, [key, rawValue]) => {
      if (typeof rawValue === "string") {
        acc[key] = rawValue;
      }
      return acc;
    }, {});
  } catch {
    return {};
  }
}

function useAsyncTableFilterRuntime(
  configs: TableQueryFieldConfig[] | undefined,
  t: TranslateFn,
  lookupScope?: TableQueryLookupScope,
) {
  const asyncFields = React.useMemo(
    () => (configs?.filter((config): config is TableQueryAsyncSelectFieldConfig => config.type === "asyncSelect") ?? []),
    [configs],
  );
  const [searchByField, setSearchByField] = React.useState<Record<string, string>>({});
  const debounceMs = React.useMemo(
    () => Math.max(300, ...asyncFields.map((field) => field.lookup.debounceMs ?? 300)),
    [asyncFields],
  );
  const debouncedSearchJson = useDebouncedValue(JSON.stringify(searchByField), debounceMs);
  const debouncedSearchByField = React.useMemo(() => safeParseSearchMap(debouncedSearchJson), [debouncedSearchJson]);

  const queries = useQueries({
    queries: asyncFields.map((field) => {
      const query = debouncedSearchByField[field.id]?.trim() ?? "";
      const limit = field.lookup.limit ?? 10;
      const context = field.lookup.context ?? lookupScope?.context;
      const workspaceId = lookupScope?.workspaceId;
      const branchId = lookupScope?.branchId;
      const enabled = (field.lookup.enabled ?? true) && Boolean(workspaceId);

      return {
        queryKey: lookupQueryKeys.list({
          type: field.lookup.type,
          query,
          limit,
          workspaceId,
          branchId,
          context,
          // Config `activeOnly` is the legacy "only active" flag; lookups are active-by-default now, so it
          // only matters when explicitly false → broaden to include inactive records.
          includeInactive: field.lookup.activeOnly === false ? true : undefined,
        }),
        queryFn: () =>
          fetchLookup(field.lookup.type, {
            q: query || undefined,
            limit,
            workspaceId,
            branchId,
            context,
            // Config `activeOnly` is the legacy "only active" flag; lookups are active-by-default now, so it
          // only matters when explicitly false → broaden to include inactive records.
          includeInactive: field.lookup.activeOnly === false ? true : undefined,
          }),
        enabled,
        staleTime: 60_000,
      };
    }),
  });

  return React.useMemo(() => {
    const runtime = new Map<string, AsyncFilterRuntimeState>();

    asyncFields.forEach((field, index) => {
      const query = queries[index];
      const options = (query?.data?.items ?? []).map((item) => ({
        value: item.id,
        label: item.label,
      }));

      runtime.set(field.id, {
        options,
        searchValue: searchByField[field.id] ?? "",
        onSearchValueChange: (value: string) => {
          setSearchByField((prev) => ({ ...prev, [field.id]: value }));
        },
        searchPlaceholder: field.lookup.searchPlaceholderKey
          ? t(field.lookup.searchPlaceholderKey as never)
          : undefined,
        mobileEmpty: field.lookup.mobileEmptyKey ? t(field.lookup.mobileEmptyKey as never) : undefined,
        loadingMessage: field.lookup.loadingMessageKey
          ? t(field.lookup.loadingMessageKey as never)
          : undefined,
        isLoading: query?.isLoading ?? false,
      });
    });

    return runtime;
  }, [asyncFields, queries, searchByField, t]);
}

export function resolveTableFilterFields(
  configs: TableQueryFieldConfig[] | undefined,
  t: TranslateFn,
  sources: TableQueryOptionSources = {},
  asyncRuntime: Map<string, AsyncFilterRuntimeState> = new Map(),
): TableFilterFieldConfig[] {
  if (!configs?.length) return [];

  return configs.map((config) => {
    if (config.type === "select" || config.type === "multiSelect") {
      const options = resolveSelectOptions(config.options, t, sources);
      const base = resolveBaseConfig(config, t);
      if (config.type === "select") {
        return {
          ...base,
          type: "select",
          key: config.key,
          defaultValue: config.defaultValue,
          options,
          creatable: config.creatable,
          formatCreateLabel: config.createLabelKey
            ? (inputValue: string) => t(config.createLabelKey as never, { value: inputValue })
            : undefined,
        };
      }
      return {
        ...base,
        type: "multiSelect",
        key: config.key,
        options,
        defaultValues: config.defaultValues,
        emptyLabel: config.emptyLabelKey ? t(config.emptyLabelKey as never) : undefined,
        creatable: config.creatable,
        formatCreateLabel: config.createLabelKey
          ? (inputValue: string) => t(config.createLabelKey as never, { value: inputValue })
          : undefined,
      };
    }

    if (config.type === "asyncSelect") {
      const base = resolveBaseConfig(config, t);
      const runtime = asyncRuntime.get(config.id);

      return {
        ...base,
        type: "asyncSelect",
        key: config.key,
        defaultValue: config.defaultValue,
        options: runtime?.options ?? [],
        searchValue: runtime?.searchValue,
        onSearchValueChange: runtime?.onSearchValueChange,
        searchPlaceholder: runtime?.searchPlaceholder,
        isLoading: runtime?.isLoading,
        loadingMessage: runtime?.loadingMessage,
        mobileEmpty: runtime?.mobileEmpty,
      };
    }

    if (config.type === "text") {
      return {
        ...resolveBaseConfig(config, t),
        type: "text",
        key: config.key,
        defaultValue: config.defaultValue,
      };
    }

    if (config.type === "boolean") {
      return {
        ...resolveBaseConfig(config, t),
        type: "boolean",
        key: config.key,
        defaultValue: config.defaultValue,
        trueLabel: config.trueLabelKey ? t(config.trueLabelKey as never) : undefined,
        falseLabel: config.falseLabelKey ? t(config.falseLabelKey as never) : undefined,
      };
    }

    if (config.type === "dateTime") {
      return {
        ...resolveBaseConfig(config, t),
        type: "dateTime",
        key: config.key,
        defaultValue: config.defaultValue,
      };
    }

    if (config.type === "numberRange") {
      return {
        id: config.id,
        type: "numberRange",
        label: t(config.labelKey as never),
        description: config.descriptionKey ? t(config.descriptionKey as never) : undefined,
        hidden: config.hidden,
        visibleWhen: config.visibleWhen,
        disabled: config.disabled,
        operators: resolveOperators(config.operators, t),
        defaultOperator: config.defaultOperator,
        minKey: config.minKey,
        maxKey: config.maxKey,
        minLabel: config.minLabelKey ? t(config.minLabelKey as never) : undefined,
        maxLabel: config.maxLabelKey ? t(config.maxLabelKey as never) : undefined,
        minPlaceholder: config.minPlaceholderKey ? t(config.minPlaceholderKey as never) : undefined,
        maxPlaceholder: config.maxPlaceholderKey ? t(config.maxPlaceholderKey as never) : undefined,
        minDefaultValue: config.minDefaultValue,
        maxDefaultValue: config.maxDefaultValue,
      };
    }

    return {
      id: config.id,
      type: "dateRange",
      label: t(config.labelKey as never),
      description: config.descriptionKey ? t(config.descriptionKey as never) : undefined,
      hidden: config.hidden,
      visibleWhen: config.visibleWhen,
      disabled: config.disabled,
      operators: resolveOperators(config.operators, t),
      defaultOperator: config.defaultOperator,
      fromKey: config.fromKey,
      toKey: config.toKey,
      fromLabel: config.fromLabelKey ? t(config.fromLabelKey as never) : undefined,
      toLabel: config.toLabelKey ? t(config.toLabelKey as never) : undefined,
      fromPlaceholder: config.fromPlaceholderKey ? t(config.fromPlaceholderKey as never) : undefined,
      toPlaceholder: config.toPlaceholderKey ? t(config.toPlaceholderKey as never) : undefined,
      fromDefaultValue: config.fromDefaultValue,
      toDefaultValue: config.toDefaultValue,
      presets: config.presets?.map((preset) => ({
        id: preset.id,
        label: t(preset.labelKey as never),
      })),
    };
  });
}

export type UseTableQueryResult = {
  queryState: DataTableQueryState;
  filterFields: TableFilterFieldConfig[];
  filterConfig: TableFilterConfig;
};

export function useTableQuery({
  config,
  t,
  optionSources,
  lookupScope,
}: {
  config: TableQueryConfig;
  t: TranslateFn;
  optionSources?: TableQueryOptionSources;
  lookupScope?: TableQueryLookupScope;
}): UseTableQueryResult {
  const asyncRuntime = useAsyncTableFilterRuntime(config.filters, t, lookupScope);

  const filterFields = React.useMemo(
    () => resolveTableFilterFields(config.filters, t, optionSources ?? {}, asyncRuntime),
    [asyncRuntime, config.filters, optionSources, t],
  );

  const queryState = useDataTableQueryState({
    fields: filterFields,
    defaultSortBy: config.defaultSortBy,
    defaultSortOrder: config.defaultSortOrder,
    defaultSearch: config.defaultSearch,
    defaultLimit: config.defaultLimit,
    defaultPage: config.defaultPage,
    urlMode: config.urlMode,
  });

  const filterConfig = React.useMemo<TableFilterConfig>(
    () => ({
      title: config.titleKey ? t(config.titleKey as never) : undefined,
      description: config.descriptionKey ? t(config.descriptionKey as never) : undefined,
      buttonLabel: config.buttonLabelKey ? t(config.buttonLabelKey as never) : undefined,
      cancelLabel: config.cancelLabelKey ? t(config.cancelLabelKey as never) : undefined,
      resetLabel: config.resetLabelKey ? t(config.resetLabelKey as never) : undefined,
      applyLabel: config.applyLabelKey ? t(config.applyLabelKey as never) : undefined,
      clearLabel: config.clearLabelKey ? t(config.clearLabelKey as never) : undefined,
      addFilterButtonTestId: config.addFilterButtonTestId,
      refreshButtonTestId: config.refreshButtonTestId,
      refreshLabel: config.refreshLabelKey ? t(config.refreshLabelKey as never) : undefined,
      search: config.search
        ? {
            value: queryState.search,
            onChange: queryState.setSearch,
            placeholder: config.search.placeholderKey ? t(config.search.placeholderKey as never) : undefined,
            testId: config.search.testId,
            debounceMs: config.search.debounceMs,
            className: config.search.className,
          }
        : undefined,
      fields: filterFields,
    }),
    [
      config.addFilterButtonTestId,
      config.applyLabelKey,
      config.buttonLabelKey,
      config.cancelLabelKey,
      config.clearLabelKey,
      config.descriptionKey,
      config.refreshButtonTestId,
      config.refreshLabelKey,
      config.resetLabelKey,
      config.search,
      config.titleKey,
      filterFields,
      queryState.search,
      queryState.setSearch,
      t,
    ],
  );

  return {
    queryState,
    filterFields,
    filterConfig,
  };
}
